import { useCallback, useMemo, useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import {
  Paper,
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  useMediaQuery,
  useTheme,
  TablePagination,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { parse } from "date-fns";

import { useGetTickets } from "../../../hooks/useGetTickets";
import TicketsInfoModal from "./components/TicketsInfoModal";
import TicketsMessageError from "./components/TicketsMessageError";
import TicketsMessageNotFound from "./components/TicketsMessageNotFound";
import TicketsMobile from "./components/TicketsMobile";
import TicketsSkeletons from "./components/TicketsSkeleton";
import { isEmpty } from "lodash";

function Ticket() {
  const [openModal, setOpenModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [orderBy, setOrderBy] = useState("recent");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("1094"));

  const document = localStorage.getItem("username");

  const { tickets, ticketsError, ticketsLoading } = useGetTickets(document);

  const handleOpenModal = useCallback((ticket) => {
    setSelectedTicket(ticket);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedTicket(null);
  }, []);

  const statusColors = useMemo(
    () => ({
      Aberta: "primary",
      Pendente: "warning",
      Fechada: "success",
      Cancelada: "error",
    }),
    []
  );

  const getStatusChip = useCallback(
    (status) => {
      return (
        <Chip
          label={status}
          color={statusColors[status] || "default"}
          size="small"
        />
      );
    },
    [statusColors]
  );

  const filteredTickets = useMemo(() => {
    if (isEmpty(tickets)) {
      return [];
    }

    return tickets.result
      .filter((ticket) => {
        const { NumeroOS, Modelo, Abertura, SituacaoDaOS } = ticket;
        const matchesSearch =
          NumeroOS.toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          Modelo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          Abertura.toLowerCase().includes(searchTerm.toLowerCase()) ||
          SituacaoDaOS.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus =
          statusFilter === "Todos" || SituacaoDaOS === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        const aberturaA = a.Abertura.split(" ")[0];
        const aberturaB = b.Abertura.split(" ")[0];

        const dateA = parse(aberturaA, "dd/MM/yyyy", new Date());
        const dateB = parse(aberturaB, "dd/MM/yyyy", new Date());

        if (orderBy === "recent") return dateB - dateA;

        return dateA - dateB;
      });
  }, [tickets, searchTerm, statusFilter, orderBy]);

  const paginatedTickets = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredTickets.slice(start, end);
  }, [filteredTickets, page, rowsPerPage]);

  if (ticketsLoading) return <TicketsSkeletons />;

  if (isEmpty(tickets)) return <TicketsMessageNotFound />;

  if (ticketsError) return <TicketsMessageError />;

  return (
    <>
      {isMobile ? (
        <TicketsMobile
          filteredTickets={filteredTickets}
          openModal={openModal}
          selectedTicket={selectedTicket}
          handleOpenModal={handleOpenModal}
          getStatusChip={getStatusChip}
          setStatusFilter={setStatusFilter}
          paginatedTickets={paginatedTickets}
          setOrderBy={setOrderBy}
          page={page}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
        />
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography
            variant="h4"
            fontWeight={600}
            marginBottom={2}
            marginTop={1}
          >
            Ordens de Serviço
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box
            display="flex"
            alignItems="flex-end"
            mb={2}
            flexWrap="wrap"
            justifyContent="space-between"
            gap={2}
          >
            <Box minWidth={250}>
              <TextField
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1 }} color="action.active" />
                  ),
                }}
                variant="standard"
                placeholder="Pesquisar"
                sx={{ border: "none" }}
                fullWidth
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Box>
            <Box display="flex" alignItems="flex-end" gap={4}>
              <FormControl variant="standard" minWidth={160}>
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={orderBy}
                  onChange={(e) => setOrderBy(e.target.value)}
                  label="Ordenar por"
                >
                  <MenuItem value="recent">Mais recente</MenuItem>
                  <MenuItem value="oldest">Mais antiga</MenuItem>
                </Select>
              </FormControl>

              <FormControl variant="standard" minWidth={120}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="Todos">Todos</MenuItem>
                  <MenuItem value="Aberta">Aberta</MenuItem>
                  <MenuItem value="Pendente">Pendente</MenuItem>
                  <MenuItem value="Fechada">Fechada</MenuItem>
                  <MenuItem value="Cancelada">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer overflowX="auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Nº da OS</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Equipamento
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Tipo de Manutenção
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Data de abertura
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTickets.length > 0
                  ? paginatedTickets.map((ticket) => {
                      const {
                        NumeroOS,
                        Modelo,
                        Abertura,
                        SituacaoDaOS,
                        TipoDeManutencao,
                      } = ticket;
                      return (
                        <TableRow
                          key={NumeroOS}
                          onClick={() => handleOpenModal(ticket)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#f5f5f5" },
                          }}
                        >
                          <TableCell>
                            <Typography variant="subtitle1">
                              {NumeroOS}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {Modelo}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {TipoDeManutencao}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ width: "1%" }}>
                              {Abertura}
                            </Typography>
                          </TableCell>
                          <TableCell>{getStatusChip(SituacaoDaOS)}</TableCell>
                        </TableRow>
                      );
                    })
                  : null}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredTickets.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
            labelRowsPerPage="OS por página"
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}
      {openModal && selectedTicket && (
        <TicketsInfoModal
          openModal={openModal}
          handleCloseModal={handleCloseModal}
          selectedTicket={selectedTicket}
          statusFilter={statusFilter}
          orderBy={orderBy}
        />
      )}
    </>
  );
}

export default Ticket;
